import { HazardScenario } from '../types/hazard.types'
import { isMeaningfulParamValue } from './hazard.constants'
import { ScenarioGridParameters } from './hazardScenarioGridParameters.utils'

export type SedimentLabel = 'NC' | 'INC' | 'RED'

export const SEDIMENT_LABEL_MAP: Record<string, SedimentLabel> = {
  'No change': 'NC',
  'Not specified': 'NC',
  Increased: 'INC',
  Reduction: 'RED',
}

export const SEDIMENT_COLORS: Record<
  SedimentLabel,
  { bg: string; color: string; hoverBg: string }
> = {
  NC: { bg: '#e8e8e8', color: '#4a4a4a', hoverBg: '#4a4a4a' },
  INC: { bg: '#fff0cc', color: '#7a4f00', hoverBg: '#7a4f00' },
  RED: { bg: '#fde8e8', color: '#8b1a1a', hoverBg: '#8b1a1a' },
}

export const SEDIMENT_DESCRIPTIONS: Record<SedimentLabel, string> = {
  NC: 'No change',
  INC: 'Increased',
  RED: 'Reduced',
}

export const getSedimentLabel = (value: string): SedimentLabel => SEDIMENT_LABEL_MAP[value] ?? 'NC'

export const isSedimentValue = (value: unknown): boolean =>
  typeof value === 'string' && value in SEDIMENT_LABEL_MAP

export const formatParamLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export const formatRowLabel = (value: ScenarioAxisValue, rowKey: string): string => {
  if (rowKey === 'slr' && typeof value === 'number') return `${Math.round(value * 100)}cm`
  return String(value)
}

export const formatParamValue = (key: string, value: string | number | boolean | null): string => {
  if (typeof value === 'number' && key === 'slr') return `${Math.round(value * 100)}cm`
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

export type ScenarioAxisValue = string | number

export type DotLegendEntry = {
  key: string
  label: string
  color: string
}

export type ScenarioCell = {
  assetTag: string
  sedimentLabel: SedimentLabel
  hasSediment: boolean
  dotColor: string | null
  scenario: HazardScenario
}

export type ScenarioGrid = {
  colValues: ScenarioAxisValue[]
  rowValues: ScenarioAxisValue[]
  colKey: string
  rowKey: string
  grid: ScenarioCell[][][]
  dotLegend: DotLegendEntry[] | null
}

const DOT_COLOR_PALETTE = [
  '#F5A623',
  '#4A90D9',
  '#D0021B',
  '#7B3FA0',
  '#2CA05A',
  '#E87722',
  '#0B7285',
  '#8D6748',
]

const hashString = (str: string): number => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) & 0xffffffff
  }
  return Math.abs(hash)
}

const buildCombinationKey = (scenario: HazardScenario, excludeKeys: Set<string>): string =>
  Object.entries(scenario.parameters)
    .filter(([k, v]) => !excludeKeys.has(k) && isMeaningfulParamValue(v))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|')

const buildCombinationLabel = (
  scenario: HazardScenario,
  excludeKeys: Set<string>,
  varyingKeys: Set<string>, // ← only keys with >1 unique value
): string =>
  Object.entries(scenario.parameters)
    .filter(([k, v]) => !excludeKeys.has(k) && varyingKeys.has(k) && isMeaningfulParamValue(v))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${formatParamLabel(k)}: ${String(v)}`)
    .join(' + ')

const toAxisValue = (val: unknown): ScenarioAxisValue | null => {
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const num = Number(val)
    return isNaN(num) ? val : num
  }
  return null
}

const resolveSedimentLabel = (sediment: unknown): SedimentLabel =>
  getSedimentLabel(sediment == null ? '' : String(sediment))

const sortAxisValues = (values: ScenarioAxisValue[]): ScenarioAxisValue[] => {
  if (values.every((v) => typeof v === 'number')) {
    return (values as number[]).sort((a, b) => a - b)
  }
  return (values as string[]).sort((a, b) => a.toString().localeCompare(b.toString()))
}

/**
 * Transforms a flat array of HazardScenario into a 2D grid structure.
 *
 * @param scenarios - Filtered scenarios — drives grid cell population
 * @param gridParameters - Resolved row/col axis keys
 * @param allScenarios - Full unfiltered set — drives legend and varyingKeys computation
 *   so the legend stays stable as filters change. Defaults to scenarios if not provided.
 */
export const buildScenarioGrid = (
  scenarios: HazardScenario[],
  gridParameters: ScenarioGridParameters,
  allScenarios: HazardScenario[] = scenarios,
): ScenarioGrid => {
  const { rowKey, colKey } = gridParameters

  const colSet = new Set<ScenarioAxisValue>()
  const rowSet = new Set<ScenarioAxisValue>()

  for (const s of scenarios) {
    const colVal = toAxisValue(s.parameters[colKey])
    const rowVal = toAxisValue(s.parameters[rowKey])
    if (colVal !== null) colSet.add(colVal)
    if (rowVal !== null) rowSet.add(rowVal)
  }

  const colValues = sortAxisValues([...colSet])
  const rowValues = sortAxisValues([...rowSet])

  const grid: ScenarioCell[][][] = rowValues.map(() => colValues.map(() => []))

  // Use allScenarios to determine sediment presence and varying keys
  // so legend stays consistent regardless of active filters
  const anyHasSediment = allScenarios.some((s) => isSedimentValue(s.parameters['sediment']))
  const excludeKeys = new Set([rowKey, colKey, 'sediment'])

  // varyingKeys: parameters that actually vary across ALL scenarios (not filtered)
  // Constants like slr:0, year:2026 are excluded from labels
  const varyingKeys = new Set(
    Object.keys(allScenarios[0]?.parameters ?? {}).filter((key) => {
      if (excludeKeys.has(key)) return false
      const values = new Set(allScenarios.map((s) => s.parameters[key]))
      return values.size > 1
    }),
  )

  // Build deterministic color map from ALL scenarios — stable across filter changes
  const combinationColorMap = new Map<string, string>()
  const combinationLabelMap = new Map<string, string>()

  if (!anyHasSediment) {
    for (const scenario of allScenarios) {
      const key = buildCombinationKey(scenario, excludeKeys)
      if (!combinationColorMap.has(key)) {
        const colorIndex = hashString(key) % DOT_COLOR_PALETTE.length
        combinationColorMap.set(key, DOT_COLOR_PALETTE[colorIndex])
        const label = buildCombinationLabel(scenario, excludeKeys, varyingKeys)
        // Fallback to display_name when no varying keys produce a label (e.g. single combination)
        combinationLabelMap.set(key, label || scenario.display_name)
      }
    }
  }

  // Populate grid cells from filtered scenarios only
  for (const scenario of scenarios) {
    const colVal = toAxisValue(scenario.parameters[colKey])
    const rowVal = toAxisValue(scenario.parameters[rowKey])

    const colIndex = colVal !== null ? colValues.indexOf(colVal) : -1
    const rowIndex = rowVal !== null ? rowValues.indexOf(rowVal) : -1

    if (colIndex === -1 || rowIndex === -1) continue

    const sediment = scenario.parameters['sediment']
    const hasSediment = isSedimentValue(sediment)

    const dotColor = !hasSediment
      ? (combinationColorMap.get(buildCombinationKey(scenario, excludeKeys)) ?? '#9e9e9e')
      : null

    grid[rowIndex][colIndex].push({
      assetTag: scenario.assetTag,
      sedimentLabel: hasSediment ? resolveSedimentLabel(sediment) : 'NC',
      hasSediment,
      dotColor,
      scenario,
    })
  }

  // Dot legend built from ALL scenarios — stable, not filtered
  // Only show entries that are present in the current filtered set
  const visibleCombinationKeys = new Set(scenarios.map((s) => buildCombinationKey(s, excludeKeys)))

  const dotLegend: DotLegendEntry[] | null = !anyHasSediment
    ? [...combinationColorMap.entries()]
        .filter(([key]) => visibleCombinationKeys.has(key))
        .map(([key, color]) => ({
          key,
          label: combinationLabelMap.get(key) ?? key,
          color,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : null

  return { colValues, rowValues, colKey, rowKey, grid, dotLegend }
}
